import { useState, useEffect } from 'react';
import { api, AdminRole } from './api';
import { useHashState, ErrorBoundary, useVersionWatcher, useBeforeUnloadWhileBusy, confirmLeaveWhileBusy } from '../../shared/src';
import {
  LoginPage,
  SetPasswordPage,
  OrganizationList,
  OrganizationDetail,
  AuditLogList,
  AdminProfile,
  AdminsPage,
} from './components';
import { setSentryAdmin, clearSentryUser } from '../../shared/src/sentry';

// Get invitation token from URL (for /set-password?token=xxx flow)
function getInvitationToken(): string | null {
  const path = window.location.pathname;
  if (path === '/set-password') {
    const params = new URLSearchParams(window.location.search);
    return params.get('token');
  }
  return null;
}

// Clear invitation token from URL and redirect to home
function clearInvitationToken(): void {
  window.history.replaceState({}, '', '/');
}

type View = 'organizations' | 'org' | 'audit-logs' | 'profile' | 'admins';
type OrgTab = 'details' | 'auth' | 'sso' | 'users' | 'permissions' | 'uploads' | 'extractions';

interface AdminInfo {
  id: string;
  email: string;
  name: string | null;
  role: AdminRole;
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(!!api.getToken());
  const [adminInfo, setAdminInfo] = useState<AdminInfo | null>(null);
  const [isLoading, setIsLoading] = useState(!!api.getToken()); // Loading if we need to fetch admin info
  const [invitationToken, setInvitationToken] = useState<string | null>(getInvitationToken);

  // One-time notice on the login screen after a session-expiry redirect.
  const [sessionNotice] = useState<string | null>(() => {
    if (typeof sessionStorage === 'undefined') return null;
    const reason = sessionStorage.getItem('post_logout_reason');
    if (reason) sessionStorage.removeItem('post_logout_reason');
    return reason === 'session_expired'
      ? 'Your session expired. Please sign in again.'
      : null;
  });

  // Silently refresh idle clients to a new deploy. Routed through the shared reload gate,
  // so it is deferred while an interruption-sensitive operation is running — in this SPA
  // that is the folder zip download (`UploadsTab` brackets it with `busyTracker`). Left
  // unconditional (runs on the login screen too) so even a long-open, unauthenticated tab
  // self-updates; the cost is one tiny /version.json GET per minute.
  useVersionWatcher();
  // The other two guards of the trio (README "Release Safety"): warn on a manual
  // reload/close, and confirm a browser back/forward — both would otherwise kill
  // a running folder download silently. In-app sidebar navigation is NOT guarded;
  // leaving the Uploads tab aborts the run (see CLAUDE.md).
  useBeforeUnloadWhileBusy();

  // Restore admin info on page load if we have a token
  useEffect(() => {
    const restoreSession = async () => {
      if (!api.getToken()) {
        setIsLoading(false);
        return;
      }

      try {
        const result = await api.getCurrentAdmin();
        setAdminInfo({
          id: result.admin.id,
          email: result.admin.email,
          name: result.admin.name,
          role: result.admin.role,
        });
        setIsAuthenticated(true);
        setSentryAdmin(result.admin.email);
        // If already authenticated and on set-password page, redirect to home
        if (invitationToken) {
          clearInvitationToken();
          setInvitationToken(null);
        }
      } catch {
        // Token is invalid or expired - clear auth state
        api.logout();
        setIsAuthenticated(false);
        setAdminInfo(null);
      } finally {
        setIsLoading(false);
      }
    };

    restoreSession();
  }, [invitationToken]);

  // Handle successful password set from invitation
  const handleSetPasswordSuccess = async () => {
    clearInvitationToken();
    setInvitationToken(null);
    // Fetch admin info after successful password set
    try {
      const result = await api.getCurrentAdmin();
      setAdminInfo({
        id: result.admin.id,
        email: result.admin.email,
        name: result.admin.name,
        role: result.admin.role,
      });
      setIsAuthenticated(true);
      setSentryAdmin(result.admin.email);
    } catch {
      // Fallback: just set authenticated, info will be fetched on next render
      setIsAuthenticated(true);
    }
  };

  // Handle back to login from set password page
  const handleBackToLogin = () => {
    clearInvitationToken();
    setInvitationToken(null);
  };

  const [hashState, setHashState] = useHashState({
    view: 'organizations' as string,
    orgId: null as string | null,
    tab: 'details' as string,
    subtab: 'users' as string,
    folder: null as string | null,
    project: null as string | null,
  }, { onBeforePop: () => confirmLeaveWhileBusy() });

  // Type-safe accessors
  const view = hashState.view as View;
  const orgTab = (hashState.tab || 'details') as OrgTab;

  const handleLogin = async (email: string, password: string) => {
    const result = await api.login(email, password);
    setAdminInfo({
      id: result.admin.id,
      email: result.admin.email,
      name: result.admin.name || null,
      role: result.admin.role as AdminRole,
    });
    setIsAuthenticated(true);
    setSentryAdmin(result.admin.email);
  };

  const handleLogout = () => {
    if (!confirmLeaveWhileBusy('A folder download is in progress. Signing out will interrupt it. Sign out anyway?')) {
      return;
    }
    api.logout();
    clearSentryUser();
    setIsAuthenticated(false);
    setAdminInfo(null);
  };

  const handleSelectOrg = (orgId: string) => {
    setHashState({ view: 'org', orgId, tab: 'details', subtab: 'users', folder: null, project: null });
  };

  const handleBackToList = () => {
    setHashState({ view: 'organizations', orgId: null, tab: 'details', subtab: 'users', folder: null, project: null });
  };

  const handleTabChange = (tab: OrgTab) => {
    setHashState({ tab, subtab: 'users', folder: null, project: null });
  };

  const handleSubtabChange = (subtab: string) => {
    setHashState({ subtab });
  };

  const handleFolderChange = (folder: string | null) => {
    setHashState({ folder });
  };

  const handleProjectChange = (project: string | null) => {
    setHashState({ project, folder: null });
  };

  const getRoleLabel = (role: AdminRole) => {
    switch (role) {
      case 'super_admin': return 'Super Admin';
      case 'admin_editor': return 'Editor';
      case 'admin_viewer': return 'Viewer';
      default: return role;
    }
  };

  // Check if admin can view the admins page (super_admin and admin_editor)
  const canViewAdmins = adminInfo && adminInfo.role !== 'admin_viewer';

  // Show loading state while restoring session
  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner" />
        <p>Loading...</p>
      </div>
    );
  }

  // Show set password page for invited admins
  if (invitationToken && !isAuthenticated) {
    return (
      <SetPasswordPage
        token={invitationToken}
        onSuccess={handleSetPasswordSuccess}
        onBackToLogin={handleBackToLogin}
      />
    );
  }

  if (!isAuthenticated) {
    return <LoginPage onLogin={handleLogin} notice={sessionNotice} />;
  }

  return (
    <ErrorBoundary>
    <div className="admin-layout">
      <aside className="sidebar">
        <div className="logo">
          <h2>Fakto Admin</h2>
        </div>
        <nav>
          <button
            className={view === 'organizations' ? 'active' : ''}
            onClick={() => setHashState({ view: 'organizations', orgId: null, tab: 'details', folder: null })}
          >
            Organizations
          </button>
          <button
            className={view === 'audit-logs' ? 'active' : ''}
            onClick={() => setHashState({ view: 'audit-logs', orgId: null, tab: 'details', folder: null })}
          >
            Audit Logs
          </button>
          {canViewAdmins && (
            <button
              className={view === 'admins' ? 'active' : ''}
              onClick={() => setHashState({ view: 'admins', orgId: null, tab: 'details', folder: null })}
            >
              Platform Admins
            </button>
          )}
        </nav>
        <div className="sidebar-footer">
          {adminInfo && (
            <div
              className={`user-info clickable ${view === 'profile' ? 'active' : ''}`}
              onClick={() => setHashState({ view: 'profile', orgId: null, tab: 'details', folder: null })}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && setHashState({ view: 'profile', orgId: null, tab: 'details', folder: null })}
            >
              <div className="user-avatar">
                {(adminInfo.name?.[0] || adminInfo.email[0]).toUpperCase()}
              </div>
              <div className="user-details">
                <span className="user-name">{adminInfo.name || adminInfo.email}</span>
                <span className="user-role">{getRoleLabel(adminInfo.role)}</span>
              </div>
            </div>
          )}
          <button onClick={handleLogout} className="logout-btn">Sign Out</button>
        </div>
      </aside>
      <main className="main-content">
        {view === 'organizations' && (
          <OrganizationList onSelectOrg={handleSelectOrg} />
        )}
        {view === 'org' && hashState.orgId && adminInfo && (
          <OrganizationDetail
            orgId={hashState.orgId}
            activeTab={orgTab}
            activeSubtab={hashState.subtab}
            currentFolderId={hashState.folder}
            selectedProjectId={hashState.project}
            onBack={handleBackToList}
            onTabChange={handleTabChange}
            onSubtabChange={handleSubtabChange}
            onFolderChange={handleFolderChange}
            onProjectChange={handleProjectChange}
            adminRole={adminInfo.role}
          />
        )}
        {view === 'audit-logs' && (
          <AuditLogList />
        )}
        {view === 'profile' && (
          <AdminProfile />
        )}
        {view === 'admins' && adminInfo && canViewAdmins && (
          <AdminsPage
            currentAdminRole={adminInfo.role}
            currentAdminId={adminInfo.id}
          />
        )}
      </main>
    </div>
    </ErrorBoundary>
  );
}

export default App;
